import React, { useEffect, useRef } from 'react'
import { useDirection } from '@hapana/shared/web/hooks/useDirection'

import { useSupportAssistant } from './support-assistant.hooks'
import SupportSidebar from './components/support-sidebar'
import OnboardingPanelContent from '../onboarding/onboarding-panel-content'
import useBannerStore from '_stores/banner.store'

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 600

const shellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  minHeight: '100dvh',
  position: 'relative',
  width: '100%',
  overflow: 'hidden', // Prevent horizontal scrollbar when sidebar opens
}

const mainStyle = (isOpen: boolean, sidebarWidth: number, isRTL: boolean): React.CSSProperties => ({
  flex: 1,
  minWidth: 0, // Allow flex item to shrink below content size
  width: '100%',
  transition: isRTL
    ? 'margin-left 300ms ease-in-out, width 300ms ease-in-out'
    : 'margin-right 300ms ease-in-out, width 300ms ease-in-out',
  // On mobile the sidebar overlays the content (no margin needed)
  marginRight: !isRTL && isOpen && !isMobileViewport() ? sidebarWidth : 0,
  marginLeft: isRTL && isOpen && !isMobileViewport() ? sidebarWidth : 0,
  overflow: 'hidden', // Prevent content from overflowing when sidebar is open
})

const sidebarContainerStyle = (
  isOpen: boolean,
  isRTL: boolean,
  width: number,
  topBannerHeight: number
): React.CSSProperties => ({
  position: 'fixed',
  top: 0,
  right: !isRTL ? (isOpen ? 0 : -width) : undefined,
  left: isRTL ? (isOpen ? 0 : -width) : undefined,
  height: '100dvh',
  paddingTop: topBannerHeight > 0 ? topBannerHeight : undefined,
  zIndex: 1250,
  transition: isRTL ? 'left 300ms ease-in-out' : 'right 300ms ease-in-out',
  boxSizing: 'border-box',
})

export const SupportAssistantShell: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { isOpen, userInfo, activePanel } = useSupportAssistant()
  const { isRTL } = useDirection()
  const topBannerHeight = useBannerStore((s) => s.topBannerHeight)
  const sidebarWidth =
    typeof window !== 'undefined' && window.innerWidth < 600 ? window.innerWidth : 400

  // Track whether the sidebar has ever been opened. Once mounted we keep it
  // mounted so close/reopen transitions are instant with no re-fetch.
  const hasBeenOpenedRef = useRef(isOpen)
  useEffect(() => {
    if (isOpen) hasBeenOpenedRef.current = true
  }, [isOpen])
  const shouldMount = hasBeenOpenedRef.current || isOpen

  // Don't render sidebar at all when user is not authenticated
  if (!userInfo) {
    return <>{children}</>
  }

  return (
    <div style={shellStyle}>
      <div
        style={mainStyle(isOpen, sidebarWidth, isRTL)}
        data-support-sidebar-open={isOpen && !isMobileViewport() ? 'true' : undefined}
      >
        {children}
      </div>
      <div
        style={{
          ...sidebarContainerStyle(isOpen, isRTL, sidebarWidth, topBannerHeight),
          width: sidebarWidth,
        }}
      >
        {shouldMount &&
          (activePanel === 'onboarding' ? <OnboardingPanelContent /> : <SupportSidebar />)}
      </div>
    </div>
  )
}

export default SupportAssistantShell
